/**
 * We should have somewhere to share common types for these objects below, but
 * for now I'll just define the minimum need here.
 */

import type { FeatureFlipAttributes } from './types'

type OBSub = {
  plan: {
    id: string
    name: string
  }
  trial: {
    isActive: boolean
  }
  interval?: string
  startDate?: string
  isCanceledAtPeriodEnd?: boolean
}

type MPSub = {
  plan: string
  product: string
  trial: {
    isActive: boolean
  }
}

type Organization = {
  id: string
  billing: {
    subscription: OBSub
    subscriptions: MPSub[]
    hasNeverBeenPayingCustomer: boolean
    canStartTrial: boolean
    gateway?: {
      gatewayType: string
    }
  }
  isOneBufferOrganization: boolean
  createdAt: string
  /** The organization actions the current account can perform. */
  allowedActions?: string[]
}

export type Account = {
  email: string
  isImpersonation: boolean
  currentOrganization: Organization
}

export type LocationData = {
  location?: {
    country?: string | null
  }
}

export function isOnBufferTrial({
  currentOrganization,
}: {
  currentOrganization: Organization
}): boolean {
  return currentOrganization?.billing?.subscription?.trial?.isActive || false
}

function isFreePlan(accountData: Account): boolean {
  const { currentOrganization } = accountData
  if (currentOrganization?.billing?.subscriptions?.length > 0) {
    return !!currentOrganization?.billing?.subscriptions?.find(
      (sub) => sub.product === 'publish' && sub.plan === 'free',
    )
  }

  return (
    !isOnBufferTrial(accountData) &&
    currentOrganization?.billing?.subscription?.plan.id === 'free'
  )
}

function isNewBuffer(accountData: Account): boolean {
  const { currentOrganization } = accountData
  return currentOrganization?.isOneBufferOrganization || false
}

function hasNeverBeenPayingCustomer(accountData: Account): boolean {
  const { currentOrganization } = accountData
  return currentOrganization?.billing?.hasNeverBeenPayingCustomer || false
}

function canStartTrial(accountData: Account): boolean {
  const { currentOrganization } = accountData
  return currentOrganization?.billing?.canStartTrial || false
}

function signUpDate(accountData: Account): number | undefined {
  const { currentOrganization } = accountData
  return currentOrganization?.createdAt
    ? new Date(currentOrganization?.createdAt).getTime()
    : undefined
}

function isImpersonation(accountData: Account): boolean {
  const { isImpersonation } = accountData
  return isImpersonation || false
}

// Keep in sync with domains/app-shell/src/exports/Navigator/utils/shouldShowAnnualUpgradeBanner.ts
export const ANNUAL_UPGRADE_DAYS_THRESHOLD_FOUR_MONTH = 120
export const ANNUAL_UPGRADE_DAYS_THRESHOLD_TWO_MONTH = 60

export function isEligibleForAnnualUpgrade(
  accountData: Account,
  daysThreshold: number = ANNUAL_UPGRADE_DAYS_THRESHOLD_FOUR_MONTH,
): boolean {
  const { currentOrganization } = accountData
  if (!currentOrganization?.isOneBufferOrganization) return false
  if (!currentOrganization?.allowedActions?.includes('manageBilling'))
    return false

  const subscription = currentOrganization?.billing?.subscription
  if (!subscription) return false

  const gateway = currentOrganization?.billing?.gateway
  const isPayingStripe =
    gateway?.gatewayType === 'stripe' &&
    subscription.plan.id !== 'free' &&
    !subscription.trial?.isActive

  if (!isPayingStripe) return false
  if (subscription.isCanceledAtPeriodEnd) return false
  if (subscription.interval !== 'month') return false

  const startDate = subscription.startDate
  if (!startDate) return false

  const MS_PER_DAY = 86_400_000
  const daysSinceStart = Math.floor(
    (Date.now() - new Date(startDate).getTime()) / MS_PER_DAY,
  )
  return daysSinceStart >= daysThreshold
}

export default function getFeatureFlipAttributesFromAccount(
  accountData?: Account | null,
  locationData: LocationData = {
    location: {
      country: null,
    },
  },
): FeatureFlipAttributes | null {
  if (!accountData) {
    return null
  }
  return {
    isFreePlan: isFreePlan(accountData),
    isOnTrial: isOnBufferTrial(accountData),
    isImpersonation: isImpersonation(accountData),
    isNewBuffer: isNewBuffer(accountData),
    location: locationData?.location?.country
      ? locationData.location.country
      : '',
    hasNeverBeenPayingCustomer: hasNeverBeenPayingCustomer(accountData),
    canStartTrial: canStartTrial(accountData),
    signUpDate: signUpDate(accountData),
    email: accountData.email,
    isEligibleForAnnualUpgrade: isEligibleForAnnualUpgrade(accountData),
    isEligibleForAnnualUpgradeTwoMonth: isEligibleForAnnualUpgrade(
      accountData,
      ANNUAL_UPGRADE_DAYS_THRESHOLD_TWO_MONTH,
    ),
  }
}
